import React, { useEffect, useState } from 'react';
import classnames from 'classnames/bind';
import { Slide as SlideAnimation } from 'react-awesome-reveal';
import { Header } from '../Header';
import { Button } from '../Button';
import { QuizNavigation } from '../QuizNavigation';
import { QuizProgressBar } from '../QuizProgressBar';
import { QuestionAnswerSlideV4 } from '../QuestionAnswerSlideV4';
import { QuestionMultiAnswerSlide } from '../QuestionMultiAnswerSlide';
import { ProfileSlideV6 } from '../ProfileSlideV6';
import { StartSlideV5 } from '../StartSlideV5';
import { TrustSlideV5 } from '../TrustSlideV5';
import { UserMixSummarySlideV2 } from '../UserMixSummarySlideV2';
import { PlanLoaderSlideV3 } from '../PlanLoaderSlideV3';
import { RiskSlideV2 } from '../RiskSlideV2';
import { EmailSlideV3 } from '../EmailSlideV3';
import { SaleV19 } from '../SaleV19';
import { Timer } from '../Timer';
import { Footer } from '../Footer';
import { AgreementText } from '../AgreementText';
import { useQuiz } from '../../shared/use-quiz';
import { useUserProperties } from '../../shared/use-user-properties';
import { useSlideEventsV2 } from '../../shared/use-slide-events-v2';
import { isExpiredPromo } from '../../shared/promo-helpers';
import {
    EMAIL_SLIDE,
    RISK_SLIDE,
    PLAN_LOADER_SLIDE,
    PROFILE_SLIDE_COMPONENT,
    QUESTION_ANSWER_SLIDE_COMPONENT,
    QUESTION_MULTI_ANSWER_SLIDE_COMPONENT,
    START_SLIDE,
    TRUST_SLIDE,
    USER_MIX_SUMMARY_SLIDE
} from '../../shared/quiz-component-types';
import { preloadImages } from '../../../lib/shared/preload-images';
import bgSrc from '../../assets/images/progress-bg.png';
import styles from './CommonPrelanderV20.css';

const cx = classnames.bind(styles);

const IMAGES = [bgSrc];

const SLIDE_COMPONENTS = {
    [QUESTION_ANSWER_SLIDE_COMPONENT]: QuestionAnswerSlideV4,
    [QUESTION_MULTI_ANSWER_SLIDE_COMPONENT]: QuestionMultiAnswerSlide,
    [PROFILE_SLIDE_COMPONENT]: ProfileSlideV6,
    [START_SLIDE]: StartSlideV5,
    [TRUST_SLIDE]: TrustSlideV5,
    [USER_MIX_SUMMARY_SLIDE]: UserMixSummarySlideV2,
    [RISK_SLIDE]: RiskSlideV2,
    [PLAN_LOADER_SLIDE]: PlanLoaderSlideV3,
    [EMAIL_SLIDE]: EmailSlideV3
};

const DELAY = 350;

function CommonPrelanderV20({
    initialPrelander,
    quizSlides
}) {
    const {
        currentSlide,
        currentSlideIndex,
        results,
        submitAnswer,
        submitTemporaryAnswer,
        temporaryAnswer,
        finished,
        nextSlide,
        backSlide,
        setEmail,
        setExpiryTimestampPromo
    } = useQuiz(quizSlides, initialPrelander);
    const quizSlidesIndexing = quizSlides.filter(slide => !slide.isNotIndexing);
    const isQuestionSlide = [QUESTION_ANSWER_SLIDE_COMPONENT, QUESTION_MULTI_ANSWER_SLIDE_COMPONENT].includes(currentSlide.component);
    const isStartSlide = currentSlide.component === START_SLIDE;
    const isEmailSlide = currentSlide.component === EMAIL_SLIDE;
    const numberOfAnsweredSlides = results.userResponses.length + (isQuestionSlide ? 1 : 0);
    const [isExpired, setIsExpired] = useState(isExpiredPromo(results.expiryTimestampPromo));
    const [selectedSubscriptionData, selectSubscription] = useState({});
    const [merchantData, setMerchantData] = useState({});

    useUserProperties(initialPrelander);

    useEffect(() => {
        preloadImages(IMAGES);
    }, []);

    const { logQuizLifecycle } = useSlideEventsV2({ currentSlideIndex: numberOfAnsweredSlides, results, currentSlide });

    const handleAnswerWithTransition = answer => () => {
        logQuizLifecycle(quizSlidesIndexing.length === numberOfAnsweredSlides);
        submitAnswer(answer);
    };

    const setExpiryTimestampDiscount = () => {
        setExpiryTimestampPromo();
        setIsExpired(false);
    };

    const handleContinue = () => {
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });
        logQuizLifecycle(quizSlidesIndexing.length === numberOfAnsweredSlides);

        if (currentSlideIndex === quizSlides.length - 1) {
            setExpiryTimestampDiscount();
        }

        nextSlide(temporaryAnswer);
    };

    const renderLeftNavigation = () => {
        if (finished) {
            if (!results.email || selectedSubscriptionData.plan) {
                return null;
            }

            return (
                <>
                    <Timer expiryTimestamp={isExpired ? null : results.expiryTimestampPromo} label='Discount is reserved for:' />
                    <Button
                        label='get my plan'
                        className={cx('buttonMyPlan', 'animation')}
                        onClick={() => document.getElementById('subscription-start-block')?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
                    />
                </>
            );
        }

        if (!isQuestionSlide) {
            return null;
        }

        return (
            <QuizNavigation
                hasBackButton={Boolean(currentSlideIndex) && !currentSlide?.isNotBackButton}
                onBack={backSlide}
                currentSlideIndex={numberOfAnsweredSlides}
                totalSlides={quizSlidesIndexing.length}
            />
        );
    };

    const renderHeader = () => {
        if (merchantData?.merchant) {
            return null;
        }

        return (
            <Header
                renderLeftNavigation={renderLeftNavigation}
                className={cx('header', { withBorder: finished, withoutPromo: isExpired && finished && Boolean(results.email), withPlan: Boolean(selectedSubscriptionData.plan) })}
                finished={finished && Boolean(results.email) && !selectedSubscriptionData.plan}
                url={window.location.pathname}
            />
        );
    };

    const renderFooter = () => {
        if (finished) {
            if (Boolean(results.email) && !selectedSubscriptionData.plan) {
                return <Footer />;
            }

            return null;
        }

        if (Boolean(currentSlide.continueButton)) {
            return (
                <SlideAnimation
                    delay={500}
                    duration={250}
                    triggerOnce
                    direction='up'
                    className={styles.slideFooter}
                    key={currentSlide.id}
                >
                    <div className={styles.slideFooterWrapper}>
                        <Button
                            label='Continue'
                            onClick={handleContinue}
                            inSlide
                            disabled={currentSlide.component === QUESTION_MULTI_ANSWER_SLIDE_COMPONENT ? !temporaryAnswer.answerIds?.length : false}
                        />
                    </div>
                </SlideAnimation>
            );
        }

        if (currentSlideIndex === 0) {
            return (
                <AgreementText />
            );
        }

        return null;
    };

    const renderContent = () => {
        if (finished) {
            return (
                <SaleV19
                    data={results}
                    expiryTimestamp={results.expiryTimestampPromo}
                    onExpirePromo={() => setIsExpired(true)}
                    isExpired={isExpired}
                    initialPrelander={initialPrelander}
                    selectSubscription={selectSubscription}
                    selectedSubscriptionData={selectedSubscriptionData}
                    merchantData={merchantData}
                    setMerchantData={setMerchantData}
                />
            );
        }

        const Slide = SLIDE_COMPONENTS[currentSlide.component];
        const resultData = (temporaryAnswer?.id || temporaryAnswer?.answerIds)
            ? temporaryAnswer
            : results.userResponses.find(userResponse => userResponse.questionId === currentSlide.id);

        return (
            <div
                key={currentSlideIndex}
                className={cx('slide', {
                    withQuestion: isQuestionSlide,
                    withStartSlide: isStartSlide,
                    withEmailSlide: isEmailSlide
                })}
                id='slide'
            >
                <Slide
                    {...currentSlide.props}
                    submitAnswer={handleAnswerWithTransition}
                    submitTemporaryAnswer={submitTemporaryAnswer}
                    userResponse={resultData}
                    onContinue={handleContinue}
                    id={currentSlide.id}
                    results={results}
                    delay={DELAY}
                    setEmail={setEmail}
                />
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <div className={cx({ stickyHeader: finished && Boolean(results.email) && !selectedSubscriptionData.plan })}>
                {renderHeader()}
                {!finished && (
                    <QuizProgressBar
                        isActive={isQuestionSlide}
                        currentSlideIndex={numberOfAnsweredSlides}
                        totalSlides={quizSlidesIndexing.length}
                    />
                )}
            </div>
            <main
                id='main'
                key={isEmailSlide ? currentSlide.id : finished}
                className={cx('main', {
                    centered: currentSlide.centered,
                    animated: finished || isEmailSlide
                })}
            >
                {renderContent()}
            </main>
            {renderFooter()}
        </div>
    );
}

export {
    CommonPrelanderV20
};
